import { isPlatformBrowser } from '@angular/common';
import { DOCUMENT, Injectable, PLATFORM_ID, inject } from '@angular/core';

const ANALYTICS_UPLOAD_SOURCES = ['picker', 'drop', 'paste', 'local_picker'] as const;
const ANALYTICS_PROCESSING_OPERATIONS = ['export', 'merge'] as const;
const ANALYTICS_ERROR_CATEGORIES = [
  'network',
  'validation',
  'unsupported',
  'client_read',
  'server',
  'unknown',
] as const;
const ANALYTICS_OUTPUT_FORMATS = [
  'pdf',
  'psd',
  'jpg',
  'jp2',
  'j2k',
  'gif',
  'png',
  'apng',
  'bmp',
  'tiff',
  'tga',
  'webp',
  'dicom',
  'svg',
  'emf',
  'emz',
  'wmf',
  'wmz',
  'ico',
  'html',
] as const;

export type AnalyticsUploadSource = (typeof ANALYTICS_UPLOAD_SOURCES)[number];
export type AnalyticsProcessingOperation = (typeof ANALYTICS_PROCESSING_OPERATIONS)[number];
export type AnalyticsErrorCategory = (typeof ANALYTICS_ERROR_CATEGORIES)[number];

export interface AnalyticsValueAllowlist {
  readonly toolSlugs: readonly string[];
  readonly contentLanguages: readonly string[];
  readonly pagePaths?: readonly string[];
}

export type AnalyticsEvents = {
  page_view: {
    page_path: string;
    content_language: string;
  };
  tool_open: {
    tool_slug: string;
  };
  file_upload_start: {
    tool_slug: string;
    upload_source: AnalyticsUploadSource;
  };
  file_upload_success: {
    tool_slug: string;
    upload_source: AnalyticsUploadSource;
  };
  file_upload_error: {
    tool_slug: string;
    upload_source: AnalyticsUploadSource;
    error_category: AnalyticsErrorCategory;
  };
  processing_start: {
    tool_slug: string;
    operation: AnalyticsProcessingOperation;
    output_format: string;
  };
  processing_success: {
    tool_slug: string;
    operation: AnalyticsProcessingOperation;
    output_format: string;
  };
  processing_error: {
    tool_slug: string;
    operation: AnalyticsProcessingOperation;
    output_format: string;
    error_category: AnalyticsErrorCategory;
  };
  file_download: {
    tool_slug: string;
    output_format: string;
  };
};

const EVENT_PARAMETER_KEYS = {
  page_view: ['page_path', 'content_language'],
  tool_open: ['tool_slug'],
  file_upload_start: ['tool_slug', 'upload_source'],
  file_upload_success: ['tool_slug', 'upload_source'],
  file_upload_error: ['tool_slug', 'upload_source', 'error_category'],
  processing_start: ['tool_slug', 'operation', 'output_format'],
  processing_success: ['tool_slug', 'operation', 'output_format'],
  processing_error: ['tool_slug', 'operation', 'output_format', 'error_category'],
  file_download: ['tool_slug', 'output_format'],
} as const satisfies {
  [Name in keyof AnalyticsEvents]: readonly (keyof AnalyticsEvents[Name])[];
};

const TOOL_SLUG_PATTERN = /^[a-z]+(?:-[a-z]+)*$/;
const LANGUAGE_PATTERN = /^[a-z]{2}$/;
const PAGE_PATH_PATTERN = /^\/(?:[a-z]+(?:-[a-z]+)*|:[a-z]+)(?:\/(?:[a-z]+(?:-[a-z]+)*|:[a-z]+))*$/;
const SCRIPT_MARKER = 'data-imagise-ga4';

type AnalyticsWindow = Window & {
  dataLayer?: unknown[][];
  gtag?: (...args: unknown[]) => void;
};

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  readonly #document = inject(DOCUMENT);
  readonly #platformId = inject(PLATFORM_ID);

  #configured = false;
  #enabled = false;
  #loadPromise: Promise<void> | null = null;
  #measurementId: string | null = null;
  #scriptLoaded = false;
  #toolSlugs = new Set<string>();
  #contentLanguages = new Set<string>();
  #pagePaths = new Set<string>();

  /** Call only after analytics consent has been granted. */
  enable(measurementId: string | null, allowlist: AnalyticsValueAllowlist): Promise<void> {
    const normalizedId = measurementId?.trim();

    if (!normalizedId || !isPlatformBrowser(this.#platformId)) {
      return Promise.resolve();
    }

    this.#toolSlugs = new Set(allowlist.toolSlugs.filter(slug => slug.length <= 64 && TOOL_SLUG_PATTERN.test(slug)));
    this.#contentLanguages = new Set(allowlist.contentLanguages.filter(language => LANGUAGE_PATTERN.test(language)));
    this.#pagePaths = new Set(
      (allowlist.pagePaths ?? []).filter(path => path.length <= 256 && PAGE_PATH_PATTERN.test(path))
    );

    if (this.#measurementId && this.#measurementId !== normalizedId) {
      return Promise.resolve();
    }

    this.#measurementId = normalizedId;
    this.#setCollectionDisabled(false);
    this.#enabled = true;

    if (this.#configured) {
      return Promise.resolve();
    }

    if (this.#scriptLoaded) {
      this.#configure();
      return Promise.resolve();
    }

    if (this.#loadPromise) {
      return this.#loadPromise;
    }

    this.#loadPromise = this.#loadScript(normalizedId);
    return this.#loadPromise;
  }

  disable(): void {
    this.#enabled = false;
    this.#setCollectionDisabled(true);
  }

  track<Name extends keyof AnalyticsEvents>(name: Name, parameters: AnalyticsEvents[Name]): void {
    if (!this.#enabled || !this.#configured || typeof parameters !== 'object' || !parameters) {
      return;
    }

    if (!Object.hasOwn(EVENT_PARAMETER_KEYS, name)) {
      return;
    }

    const keys: readonly string[] = EVENT_PARAMETER_KEYS[name];
    const eventParameters: Record<string, string> = {};

    for (const key of keys) {
      const value = Reflect.get(parameters, key);
      if (typeof value !== 'string' || !this.#isAllowedParameterValue(key, value)) {
        return;
      }
      eventParameters[key] = value;
    }

    const page = this.#normalizePage(
      name === 'page_view' ? eventParameters['page_path'] : this.#browserWindow()?.location.pathname
    );
    if (!page || !this.#isAllowedPagePath(page.path)) {
      return;
    }

    if (name === 'page_view') {
      eventParameters['page_path'] = page.path;
    }
    eventParameters['page_location'] = page.location;

    try {
      this.#gtag()?.('event', name, eventParameters);
    } catch {
      // A blocked or unavailable analytics runtime must not break the app.
    }
  }

  #isAllowedParameterValue(key: string, value: string): boolean {
    switch (key) {
      case 'page_path':
        return Boolean(value);
      case 'content_language':
        return this.#contentLanguages.has(value);
      case 'tool_slug':
        return this.#toolSlugs.has(value);
      case 'output_format':
        return (ANALYTICS_OUTPUT_FORMATS as readonly string[]).includes(value);
      case 'upload_source':
        return (ANALYTICS_UPLOAD_SOURCES as readonly string[]).includes(value);
      case 'operation':
        return (ANALYTICS_PROCESSING_OPERATIONS as readonly string[]).includes(value);
      case 'error_category':
        return (ANALYTICS_ERROR_CATEGORIES as readonly string[]).includes(value);
      default:
        return false;
    }
  }

  #isAllowedPagePath(path: string): boolean {
    if (this.#pagePaths.has(path)) {
      return true;
    }

    const segments = path.split('/').filter(Boolean);
    if (segments.length === 0) {
      return true;
    }

    if (this.#contentLanguages.has(segments[0])) {
      return segments.length === 1 || (segments.length === 2 && this.#toolSlugs.has(segments[1]));
    }

    return segments.length === 1 && this.#toolSlugs.has(segments[0]);
  }

  #loadScript(measurementId: string): Promise<void> {
    const analyticsWindow = this.#browserWindow();
    if (!analyticsWindow || !this.#document.head) {
      return Promise.resolve();
    }

    analyticsWindow.dataLayer ??= [];
    analyticsWindow.gtag ??= (...args: unknown[]) => {
      analyticsWindow.dataLayer?.push(args);
    };

    const script = this.#document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    script.setAttribute(SCRIPT_MARKER, '');

    return new Promise(resolve => {
      let settled = false;
      const finish = (loaded: boolean): void => {
        if (settled) {
          return;
        }

        settled = true;
        this.#scriptLoaded = loaded;
        if (loaded) {
          this.#configure();
        }
        resolve();
      };

      script.addEventListener('load', () => finish(true), { once: true });
      script.addEventListener('error', () => finish(false), { once: true });

      try {
        this.#document.head.appendChild(script);
      } catch {
        finish(false);
      }
    });
  }

  #configure(): void {
    if (!this.#enabled || this.#configured || !this.#scriptLoaded || !this.#measurementId) {
      return;
    }

    const gtag = this.#gtag();
    if (!gtag) {
      return;
    }

    try {
      const currentPage = this.#normalizePage(this.#browserWindow()?.location.pathname);
      const page = currentPage && this.#isAllowedPagePath(currentPage.path) ? currentPage : this.#normalizePage('/');
      if (!page) {
        return;
      }

      gtag('js', new Date());
      gtag('config', this.#measurementId, {
        send_page_view: false,
        page_location: page.location,
      });
      this.#configured = true;
    } catch {
      // A blocked or unavailable analytics runtime must not break the app.
    }
  }

  #normalizePage(pagePath: string | undefined): { path: string; location: string } | null {
    const analyticsWindow = this.#browserWindow();
    if (!analyticsWindow || !pagePath) {
      return null;
    }

    try {
      const url = new URL(pagePath, analyticsWindow.location.origin);
      if (url.origin !== analyticsWindow.location.origin) {
        return null;
      }

      const path = (url.pathname || '/').replace(/;[^/]*/g, '');
      return {
        path,
        location: `${url.origin}${path}`,
      };
    } catch {
      return null;
    }
  }

  #gtag(): AnalyticsWindow['gtag'] {
    const gtag = this.#browserWindow()?.gtag;
    return typeof gtag === 'function' ? gtag : undefined;
  }

  #setCollectionDisabled(disabled: boolean): void {
    const analyticsWindow = this.#browserWindow();
    if (analyticsWindow && this.#measurementId) {
      Reflect.set(analyticsWindow, `ga-disable-${this.#measurementId}`, disabled);
    }
  }

  #browserWindow(): AnalyticsWindow | null {
    if (!isPlatformBrowser(this.#platformId)) {
      return null;
    }

    return this.#document.defaultView as AnalyticsWindow | null;
  }
}
